<template>
  <b-card>
    <layout-loading v-if="itemLoading" />
    <b-form v-if="render">
      <b-row>
        <b-col md="6">
          <b-form-group
            :label="$t('general.title')"
            label-for="theme-setting-title"
            :state="validateState('title')"
          >
            <b-form-input
              id="theme-setting-title"
              v-model="form.title"
              :label="$t('general.title')"
              aria-describedby="input-title-feedback"
            />
            <b-form-invalid
              id="input-title-feedback"
              :label="$t('general.title')"
              :validator="$v.form.title"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('themeSettings.language')"
            label-for="page-lang"
          >
            <languageSelector
              v-model="form.lang"
              @change-selected-language="changeSelectedLanguage"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.type')"
            label-for="themeSettings-add-type"
            :state="validateState('type')"
          >
            <v-select-custom
              id="general-type"
              v-model="form.type"
              :options="themeSettingTypes"
              :custom-props="{ disabled: true }"
            />
            <b-form-invalid
              id="input-type-feedback"
              :label="$t('general.type')"
              :validator="$v.form.type"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.slug')"
            label-for="theme-setting-slug"
          >
            <b-form-input
              id="theme-setting-slug"
              v-model="form.slug"
              :state="validateState('slug')"
              aria-describedby="input-slug-feedback"
            />
            <b-form-invalid
              id="input-slug-feedback"
              :label="$t('general.slug')"
              :validator="$v.form.slug"
            />
          </b-form-group>
        </b-col>
        <b-col
          v-if="form.type === 'phrase'"
          md="12"
        >
          <b-form-group
            :label="$t('themeSettings.phrase')"
            label-for="theme-setting-phrase"
          >
            <b-form-textarea
              id="theme-setting-phrase"
              v-model="form.phrase"
              :state="validateState('phrase')"
              aria-describedby="input-phrase-feedback"
            />
            <b-form-invalid
              id="input-phrase-feedback"
              :label="$t('themeSettings.phrase')"
              :validator="$v.form.phrase"
            />
          </b-form-group>
        </b-col>
        <b-col
          v-if="form.type === 'richText'"
          md="12"
          :class="{ 'collapse-border': form.type === 'richText' }"
        >
          <b-form-group
            label-for="theme-setting-richText"
            class="mb-2"
            :state="validateState('richText')"
          >
            <CkeditorBasic
              id="themeSettings-richText"
              v-model="form.richText"
              :label="$t('themeSettings.richText')"
              :state="validateState('richText')"
            />
            <b-form-invalid
              id="input-richText-feedback"
              :label="$t('themeSettings.richText')"
              :validator="$v.form.richText"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col
          v-if="form.type === 'file'"
          cols="12"
        >
          <b-file-input
            id="themeSettingFile"
            v-model="form.settingFile"
            :state="validateState('settingFile')"
            :alt.sync="form.settingFileAlt"
            :alt-validator="$v.form.settingFileAlt"
          >
            <template #invalidFeedback>
              <b-form-invalid
                id="input-themeSettingFile-feedback"
                :label="$t('themeSettings.file')"
                :validator="$v.form.settingFile"
              />
            </template>
          </b-file-input>
        </b-col>
        <b-col
          cols="12"
          class="mt-50"
        >
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            class="mr-1"
            :disabled="isSubmit"
            @click="submitForm"
          >
            {{ isSubmit ? $t("general.isSubmit") : $t("general.submit") }}
          </b-button>
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-secondary"
            @click="$router.go(-1)"
          >
            {{ $t("general.back") }}
          </b-button>
        </b-col>
      </b-row>
    </b-form>
  </b-card>
</template>
<script>
import {
  BButton,
  BCard,
  BCol,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BRow,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { required, minLength, requiredIf } from 'vuelidate/lib/validators'
import languageSelector from '@/components/language-selector.vue'
import BFormInvalid from '@/components/BFormInvalid/index.vue'
import BFileInput from '@/components/BFileInput/index.vue'
import LayoutLoading from '@/components/LayoutLoading.vue'
import VSelectCustom from '@/components/VSelectCustom/index.vue'
import CkeditorBasic from '@/components/CkeditorBasic/index.vue'
import useThemeSettingsList from './useThemeSettingsList'

export default {
  name: 'ThemeSettingEdit',
  components: {
    BButton,
    BCard,
    BCol,
    BFileInput,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalid,
    BFormTextarea,
    BRow,
    CkeditorBasic,
    LayoutLoading,
    VSelectCustom,
    languageSelector,
  },
  directives: {
    Ripple,
  },
  validations() {
    return {
      form: {
        title: { required, minLength: minLength(3) },
        slug: { required, minLength: minLength(3) },
        type: { required },
        settingFile: {
          required: requiredIf(function () {
            return this.form.type === 'file'
          }),
        },
        settingFileAlt: {
          required: requiredIf(function () {
            return this.form.type === 'file'
          }),
        },
        phrase: {
          required: requiredIf(function () {
            return this.form.type === 'phrase'
          }),
        },
        richText: {
          required: requiredIf(function () {
            return this.form.type === 'richText'
          }),
        },
      },
    }
  },
  setup() {
    const {
      convertResource,
      errorManager,
      fetchItem,
      fetchTags,
      form,
      handleUpload,
      isSubmit,
      item,
      itemLoading,
      tags,
      themeSettingTypes,
      updateItem,
    } = useThemeSettingsList()

    return {
      convertResource,
      errorManager,
      fetchItem,
      fetchTags,
      form,
      handleUpload,
      isSubmit,
      item,
      itemLoading,
      tags,
      themeSettingTypes,
      updateItem,
    }
  },
  computed: {
    itemId() {
      return this.$router.currentRoute.params.id
    },
    render() {
      return this.item ? this.item && Object.keys(this.item).length : true
    },
  },
  created() {
    this.fetchItem(this.itemId)
  },
  methods: {
    changeSelectedLanguage(val) {
      this.form = this.convertResource(this.item, val)
    },
    validateState(name) {
      const { $dirty, $error } = this.$v.form[name]
      return $dirty ? !$error : null
    },
    submitForm() {
      this.$v.form.$touch()
      if (this.$v.form.$anyError) {
        this.errorManager('validator.validationError')
        setTimeout(() => document.getElementsByClassName('is-invalid')[0]
          .scrollIntoView({ behavior: 'smooth', block: 'center' }), 500)
        return
      }
      const uploadList = ['settingFile']
      this.handleUpload(this.form, uploadList).then(newForm => {
        this.sendData(newForm)
      })
    },
    sendData(form) {
      let value = null
      let fileId = null
      if (form.type === 'phrase') value = form.phrase
      if (form.type === 'richText') value = form.richText
      if (form.type === 'file') fileId = form.settingFile._id
      const data = {
        title: form.title,
        slug: form.slug,
        type: form.type._id,
      }
      if (fileId) {
        data.fileId = this.form.settingFile._id
      } else {
        data.value = value
      }
      this.updateItem(this.itemId, data, this.form.lang)
    },
  },
}
</script>
